import { useState, useEffect } from 'react';
import { collection, query, orderBy, onSnapshot, deleteDoc, doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../firebase';
import { Star, Loader2, Search, Trash2, CheckCircle2, X } from 'lucide-react';

export default function AdminReviews() {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [replyingTo, setReplyingTo] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [savingReply, setSavingReply] = useState(false);

  useEffect(() => {
    const q = query(collection(db, 'reviews'), orderBy('updatedAt', 'desc'));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setReviews(data);
      setLoading(false);
    }, (error) => {
      console.error("Error fetching reviews:", error);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const handleDelete = async (id) => {
    if (window.confirm('هل أنت متأكد من حذف هذا التقييم؟')) {
      try {
        await deleteDoc(doc(db, 'reviews', id));
      } catch (error) {
        console.error("Error deleting review:", error);
      }
    }
  };

  const handleReply = async (review) => {
    try {
      setSavingReply(true);
      await updateDoc(doc(db, 'reviews', review.id), {
        adminReply: replyText,
        repliedAt: serverTimestamp()
      });
      setReplyingTo(null);
      setReplyText('');
    } catch (error) {
      console.error("Error replying to review:", error);
    } finally {
      setSavingReply(false);
    }
  };

  const filteredReviews = reviews.filter(r =>
    r.userName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    r.reviewText?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    r.targetId?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="space-y-6" dir="rtl">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 dark:text-white">إدارة تقييمات الدورات</h1>
          <p className="text-slate-500 dark:text-zinc-400 mt-1">متابعة آراء الطلاب المسجلين والرد عليها</p>
        </div>
      </div>

      <div className="bg-white dark:bg-zinc-900 rounded-2xl shadow-sm border border-slate-200 dark:border-white/10 p-6">
        <div className="flex mb-6">
          <div className="relative w-full max-w-md">
            <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
            <input
              type="text"
              placeholder="البحث في التقييمات..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-4 pr-10 py-3 bg-slate-50 dark:bg-black/50 border border-slate-200 dark:border-white/10 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all dark:text-white"
            />
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center items-center py-20">
            <Loader2 className="w-10 h-10 animate-spin text-primary-500" />
          </div>
        ) : filteredReviews.length === 0 ? (
          <div className="text-center py-20 text-slate-500 dark:text-zinc-400 font-medium bg-slate-50 dark:bg-black/20 rounded-xl border border-dashed border-slate-300 dark:border-zinc-700">
            لا توجد تقييمات مطابقة لبحثك.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredReviews.map((review) => (
              <div key={review.id} className="bg-slate-50 dark:bg-black/30 border border-slate-200 dark:border-white/5 rounded-2xl p-5 hover:shadow-md transition-shadow">

                <div className="flex justify-between items-start mb-4">
                  <div className="flex items-center gap-3">
                    {review.userPhoto ? (
                      <img src={review.userPhoto} alt="avatar" className="w-10 h-10 rounded-full object-cover border border-slate-200 dark:border-white/10" />
                    ) : (
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary-500 to-purple-600 text-white flex items-center justify-center font-bold">
                        {review.userName?.charAt(0)}
                      </div>
                    )}
                    <div>
                      <h4 className="font-bold text-slate-900 dark:text-white text-md">{review.userName}</h4>
                      <p className="text-md text-slate-500 dark:text-zinc-400">
                        لـ: {review.targetType === 'course' ? 'الدورة' : 'الدرس'} ({review.targetId})
                      </p>
                    </div>
                  </div>
                  <button onClick={() => handleDelete(review.id)} className="p-2 text-red-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-500/10 rounded-lg transition-colors">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>

                <div className="flex items-center gap-1 mb-3">
                  {[1, 2, 3, 4, 5].map(s => (
                    <Star key={s} className={`w-4 h-4 ${s <= review.rating ? 'fill-amber-400 text-amber-400' : 'text-slate-300 dark:text-zinc-600'}`} />
                  ))}
                  <span className="text-md font-medium text-slate-500 dark:text-zinc-400 mr-2">
                    {new Date(review.updatedAt?.seconds * 1000).toLocaleDateString('ar-EG')}
                  </span>
                </div>

                <p className="text-slate-700 dark:text-zinc-300 text-md leading-relaxed mb-4">
                  {review.reviewText || <span className="text-slate-400 italic">"بدون تعليق"</span>}
                </p>

                {replyingTo === review.id ? (
                  <div className="mt-4 p-4 bg-white dark:bg-zinc-900 rounded-xl border border-primary-200 dark:border-primary-500/30">
                    <textarea
                      className="w-full bg-slate-50 dark:bg-black p-3 border border-slate-200 dark:border-white/10 rounded-lg text-md mb-3 outline-none focus:ring-1 focus:ring-primary-500 dark:text-white"
                      placeholder="اكتب ردك هنا..."
                      rows={3}
                      value={replyText}
                      onChange={(e) => setReplyText(e.target.value)}
                    ></textarea>
                    <div className="flex gap-2 justify-end">
                      <button onClick={() => setReplyingTo(null)} className="px-3 py-1.5 text-slate-500 hover:text-slate-700 dark:hover:text-white text-md font-medium">إلغاء</button>
                      <button onClick={() => handleReply(review)} disabled={savingReply || !replyText.trim()} className="px-4 py-1.5 bg-primary-600 hover:bg-primary-500 text-white rounded-lg text-md font-bold flex items-center gap-2">
                        {savingReply ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />} إرسال الرد
                      </button>
                    </div>
                  </div>
                ) : review.adminReply ? (
                  <div className="mt-4 p-4 bg-primary-50 dark:bg-primary-500/10 rounded-xl relative border border-primary-100 dark:border-primary-500/20">
                    <span className="text-md font-bold text-primary-600 dark:text-primary-400 block mb-1">رد الإدارة:</span>
                    <p className="text-md text-slate-700 dark:text-zinc-300">{review.adminReply}</p>
                    <div className="absolute top-3 left-3 flex gap-2">
                      <button onClick={() => { setReplyText(review.adminReply); setReplyingTo(review.id); }} className="text-md text-primary-600 hover:underline">تعديل</button>
                    </div>
                  </div>
                ) : (
                  <div className="mt-4 border-t border-slate-200 dark:border-white/5 pt-3">
                    <button onClick={() => { setReplyingTo(review.id); setReplyText(''); }} className="text-md font-bold text-primary-600 dark:text-primary-400 hover:text-primary-700 transition">
                      + إضافة رد
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
